import { skipToken } from "@reduxjs/toolkit/query";
import type {
  PlaybookCriterionNode,
  PlaybookNode,
  PlaybookSectionNode,
} from "@repo/app-types/entities/playbook";
import type { FeedbackWithCurrentGrade } from "@repo/app-types/entities/recordingAssessmentFeedback";
import { Size } from "@repo/ui/helpers/helpers";
import { SlideoverContainer } from "@repo/ui/Slideover/slideoverContainer";
import { type FC, useState } from "react";
import { usePlaybookTeammateGradingQuery } from "../../api/entities/performancePlaybook.js";
import { GradingValidationDialogContainer } from "../../features/teammatePerformance/gradingValidationDialogContainer.js";
import { GraderFeedbackSlideoverDialog } from "./graderFeedbackSlideoverDialog.js";

interface GraderFeedbackSlideoverContainerProps {
  isOpen: boolean;
  onClose: () => void;
  feedbacks: readonly FeedbackWithCurrentGrade[] | undefined;
  node: PlaybookNode;
  onGenerateContext?: (criteria: PlaybookCriterionNode) => void;
  isGeneratingContext?: boolean;
  contextError?: string;
  onUpdateCriterion?: (criteria: Partial<PlaybookCriterionNode>) => void;
  onUpdateSection?: (section: Partial<PlaybookSectionNode>) => void;
}

export const GraderFeedbackSlideoverContainer: FC<
  GraderFeedbackSlideoverContainerProps
> = ({
  isOpen,
  onClose,
  feedbacks,
  node,
  onUpdateCriterion,
  onUpdateSection,
}) => {
  const [selectedFeedback, setSelectedFeedback] =
    useState<FeedbackWithCurrentGrade | null>(null);

  // Get IDs for the validation dialog
  const accountId = selectedFeedback?.account_id || null;
  const teammateId = selectedFeedback?.teammate_id || null;
  const playbookId = selectedFeedback?.recording_assessment_template_id || null;

  // The feedback only carries the (teammate, account) tuple, so resolve the
  // grading id the validation dialog is keyed by. The tuple maps to the unsplit
  // grading, matching the editor's pre-split behaviour.
  const { data: grading } = usePlaybookTeammateGradingQuery(
    accountId && teammateId && playbookId
      ? { playbookId, teammateId, accountId }
      : skipToken,
  );
  const gradingId = grading?.id ?? null;

  const [otherSectionOrCriteriaId, setOtherSectionOrCriteriaId] = useState<
    string | null
  >(null);

  return (
    <>
      <SlideoverContainer
        isOpen={isOpen}
        onOpenChange={(newIsOpen) => {
          if (!newIsOpen) {
            onClose();
          }
        }}
        size={Size.LARGE}
        isDismissable
      >
        <GraderFeedbackSlideoverDialog
          feedbacks={feedbacks}
          node={node}
          onFeedbackClick={(feedback) => setSelectedFeedback(feedback)}
          onUpdateCriterion={onUpdateCriterion}
          onUpdateSection={onUpdateSection}
        />
      </SlideoverContainer>

      {/* Validation slideover when feedback is selected */}
      <SlideoverContainer
        isOpen={!!selectedFeedback && !!gradingId}
        onOpenChange={(newIsOpen) => {
          if (!newIsOpen) {
            setSelectedFeedback(null);
          }
        }}
        isDismissable
      >
        {selectedFeedback && gradingId ? (
          <GradingValidationDialogContainer
            gradingId={gradingId}
            sectionOrCriteriaId={otherSectionOrCriteriaId ?? node.id}
            onCriteriaSelect={(criteriaId) => {
              setOtherSectionOrCriteriaId(criteriaId);
            }}
          />
        ) : null}
      </SlideoverContainer>
    </>
  );
};
